'use client';

import { useState } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import { Field } from '../../../components/field';
import { IconButton } from '../../../components/icon-button';
import { Input } from '../../../components/input';
import { Textarea } from '../../../components/textarea';
import { Tooltip } from '../../../components/tooltip';
import { toasterApi } from '../../toast/ToastProvider';
import { Tick01Icon, Copy01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';

export type InternalNetworkCopyableReadonlyFieldProps = {
  label: string;
  value: string;
  rows?: number;
  minHeight?: string;
  copyAriaLabel: string;
};

export function InternalNetworkCopyableReadonlyField({
  label,
  value,
  rows = 4,
  minHeight = '120px',
  copyAriaLabel,
}: InternalNetworkCopyableReadonlyFieldProps) {
  const tAdmin = useTranslations(
    'designSystem.networkSettings.internalConnection',
  );
  const tCommon = useTranslations('designSystem.common');
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    if (!value || typeof navigator === 'undefined' || !navigator.clipboard) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (error: unknown) {
      console.error(
        new Error('Failed to copy value to clipboard', { cause: error }),
      );
      toasterApi.error({
        title: tCommon('error'),
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  return (
    <Field.Root w="full">
      <Field.Label>{label}</Field.Label>
      <HStack gap={2} alignItems="flex-start" w="full">
        <Tooltip title={value} positioning={{ strategy: 'fixed' }}>
          <Box flex={1} minW={0} w="full">
            {rows === 1 ? (
              <Input
                readOnly
                value={value}
                fontFamily="mono"
                fontSize="xs"
                w="full"
              />
            ) : (
              <Textarea
                readOnly
                value={value}
                rows={rows}
                w="full"
                fontFamily="mono"
                fontSize="xs"
                css={{
                  resize: 'vertical',
                  minHeight,
                  overflowY: 'auto',
                }}
              />
            )}
          </Box>
        </Tooltip>
        <Tooltip title={copied ? tAdmin('copied') : copyAriaLabel}>
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={copyAriaLabel}
            onClick={() => void handleCopy()}
            flexShrink={0}
          >
            {copied ? <Tick01Icon size={16} /> : <Copy01Icon size={16} />}
          </IconButton>
        </Tooltip>
      </HStack>
    </Field.Root>
  );
}
